import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { CsvAsStringArrays } from '@malou-io/package-utils';

import { LightRestaurant } from '#modules/restaurants/models/restaurant';
import { AggregatedBoostersStatisticsDataV2 } from '#modules/statistics-boosters/models/aggregated-booster.interface';
import {
    AbstractCsvService,
    DataWithNilExcluded,
} from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';

interface Data {
    restaurants: LightRestaurant[];
    boostersData: AggregatedBoostersStatisticsDataV2 | undefined;
}

interface Options {
    restaurants: LightRestaurant[];
}

@Injectable()
export class AggregatedBoostersReviewsCountCsvInsightV2Service extends AbstractCsvService<Data, Options> {
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);

    protected _getData$({ restaurants }: Options): Observable<Data> {
        return of({
            restaurants,
            boostersData: this._aggregatedStatisticsStore.boosterStatsDataV2(),
        });
    }

    protected override _isDataValid({ boostersData, restaurants }: Data): boolean {
        return !!boostersData && restaurants.length > 0;
    }

    protected override _getCsvHeaderRow(): string[] {
        return ['Location', 'Location Internal Name', 'Location Address', 'Private Review Count', 'Collected Reviews Estimate'];
    }

    protected override _getCsvDataRows(data: DataWithNilExcluded<Data>): CsvAsStringArrays {
        const { boostersData, restaurants } = data;
        return restaurants.map((restaurant) => {
            const estimatedReviewCount = boostersData.totemReviewsPerRestaurant.reviewsPerPlatform.reduce((acc, platform) => {
                const platformReviews = platform.reviewsCount.find((review) => review.restaurantId === restaurant.id);
                return acc + ((platformReviews?.totemReviewsCount ?? 0) + (platformReviews?.wofReviewsCount ?? 0));
            }, 0);
            const privateReviewCount = boostersData.totemReviewsPerRestaurant.privateReviewsPerRating.reduce((acc, rating) => {
                const ratingReviews = rating.reviewsCount.find((review) => review.restaurantId === restaurant.id);
                return acc + (ratingReviews?.count ?? 0);
            }, 0);
            return [
                restaurant.name ?? '-',
                restaurant.internalName ?? '-',
                restaurant.address?.getFullFormattedAddress() ?? '-',
                privateReviewCount.toString() ?? '0',
                estimatedReviewCount.toString() ?? '0',
            ];
        });
    }
}
